import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { Form } from "./FormService";
import { Submission } from "./SubmissionService";

// GraphQL queries
const SUBMISSION_WEBHOOK_RSP_LIST_QUERY = gql`
  query SubmissionWebhookRspList(
    $filter: SubmissionWebhookRspFilter
    $params: PaginationAndSorting
  ) {
    submissionWebhookRspList(filter: $filter, params: $params) {
      code
      data {
        id
        formId
        submissionId
        responseCode
        responseMessage
        waitingTimeMs
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const SUBMISSION_WEBHOOK_RSP_GET_QUERY = gql`
  query SubmissionWebhookRspGet($id: ID) {
    submissionWebhookRspGet(id: $id) {
      code
      data {
        id
        formId
        submissionId
        responseCode
        responseMessage
        waitingTimeMs
        createdAt
        updatedAt
        form {
          id
          name
          path
          status
        }
        submission {
          id
          formId
          submissionDttm
          status
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const SUBMISSION_WEBHOOK_RSP_GET_MANY_QUERY = gql`
  query SubmissionWebhookRspGetMany($ids: [ID]) {
    submissionWebhookRspGetMany(ids: $ids) {
      code
      data {
        id
        formId
        submissionId
        responseCode
        responseMessage
        waitingTimeMs
        createdAt
        updatedAt
        form {
          id
          name
          path
          status
        }
        submission {
          id
          formId
          submissionDttm
          status
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const SUBMISSION_WEBHOOK_RSP_UPDATE_MUTATION = gql`
  mutation SubmissionWebhookRspUpdate(
    $where: SubmissionWebhookRspWhereInput
    $params: SubmissionWebhookRspInput
  ) {
    submissionWebhookRspUpdate(where: $where, params: $params) {
      code
      data {
        id
        formId
        submissionId
        responseCode
        responseMessage
        waitingTimeMs
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const SUBMISSION_WEBHOOK_RSP_REMOVE_MUTATION = gql`
  mutation SubmissionWebhookRspRemove($where: SubmissionWebhookRspWhereInput) {
    submissionWebhookRspRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const SUBMISSION_WEBHOOK_RSP_REMOVE_MANY_MUTATION = gql`
  mutation SubmissionWebhookRspRemoveMany(
    $where: [SubmissionWebhookRspWhereInput]
  ) {
    submissionWebhookRspRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types
export interface SubmissionWebhookRsp {
  id?: string | undefined;
  formId?: string | undefined;
  submissionId?: string | undefined;
  responseCode?: number | undefined;
  responseMessage?: string | undefined;
  waitingTimeMs?: string | undefined;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;
  form?: Form;
  submission?: Submission;
}

export interface SubmissionWebhookRspInput {
  id?: string | null | undefined;
  formId?: string | null | undefined;
  submissionId?: string | null | undefined;
  responseCode?: number | null | undefined;
  responseMessage?: string | null | undefined;
  waitingTimeMs?: string | null | undefined;
}

export interface SubmissionWebhookRspFilter {
  id?: string[] | null | undefined;
  formId?: string[] | null | undefined;
  submissionId?: string[] | null | undefined;
  responseCode?: number[] | null | undefined;
}

export interface SubmissionWebhookRspWhereInput {
  id?: string | undefined;
}

/**
 * Submission Webhook Response service that handles all submission webhook response-related API calls
 * Supports operations: list, get, getMany, update, remove, removeMany
 */
export class SubmissionWebhookRspService {
  /**
   * Get a list of submission webhook responses with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: SubmissionWebhookRspFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      SUBMISSION_WEBHOOK_RSP_LIST_QUERY,
      {
        filter: filter || {},
        params: {
          pagination: pagination || undefined,
          sortBy: sortBy || undefined,
        },
      }
    );
    return result;
  }

  /**
   * Get a single submission webhook response by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      SUBMISSION_WEBHOOK_RSP_GET_QUERY,
      {
        id: id,
      }
    );
    return result;
  }

  /**
   * Get multiple submission webhook responses by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      SUBMISSION_WEBHOOK_RSP_GET_MANY_QUERY,
      {
        ids: ids,
      }
    );
    return result;
  }

  /**
   * Update an existing submission webhook response
   */
  static async update(
    where: SubmissionWebhookRspWhereInput,
    params: SubmissionWebhookRspInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      SUBMISSION_WEBHOOK_RSP_UPDATE_MUTATION,
      {
        where,
        params,
      }
    );
    return result;
  }

  /**
   * Remove/delete a submission webhook response
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      SUBMISSION_WEBHOOK_RSP_REMOVE_MUTATION,
      {
        where: { id },
      }
    );
    return result;
  }

  /**
   * Remove/delete multiple submission webhook responses
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(
      SUBMISSION_WEBHOOK_RSP_REMOVE_MANY_MUTATION,
      {
        where,
      }
    );
    return result;
  }
}
